import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { getAppHelpReply } from "../utils/appHelpChat.js";
import { isClaudeClassificationAvailable } from "../lockbox";
import { useLanguage } from "../i18n/LanguageContext.js";


/**
 * Always-accessible help center with guided (and optional AI) chat.
 *
 * @param {{
 *   onBack: () => void,
 *   habitName?: string,
 *   currentStreak?: number,
 * }} props
 */
export function HelpCenterScreen({ onBack, habitName = "Procsystim", currentStreak = 0 }) {
  const { t } = useLanguage();
  const [messages, setMessages] = useState([{ role: "assistant", text: t("help_intro") }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const sendText = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || loading) return;
      const next = [...messages, { role: "user", text: trimmed }];
      setMessages(next);
      setInput("");
      setLoading(true);
      try {
        const reply = await getAppHelpReply(next, { habitName, currentStreak });
        setMessages((prev) => [...prev, { role: "assistant", text: reply }]);
      } finally {
        setLoading(false);
        setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 80);
      }
    },
    [loading, messages, habitName, currentStreak],
  );

  const send = useCallback(() => void sendText(input), [input, sendText]);

  useEffect(() => {
    setTimeout(() => scrollRef.current?.scrollToEnd({ animated: false }), 100);
  }, []);

  return (
    <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === "ios" ? "padding" : undefined}>
      <View style={styles.header}>
        <Text style={styles.title}>{t("help_title")}</Text>
        <Pressable onPress={onBack}>
          <Text style={styles.back}>{t("generic_back")}</Text>
        </Pressable>
      </View>

      <Text style={styles.subtitle}>
        {t("help_subtitle")}{" "}
        {isClaudeClassificationAvailable()
          ? t("help_ai_connected")
          : t("help_offline")}
      </Text>

      <ScrollView ref={scrollRef} style={styles.chatScroll} contentContainerStyle={styles.chatContent}>
        {messages.map((m, i) => (
          <View key={`h_${i}`} style={[styles.bubble, m.role === "user" ? styles.bubbleUser : styles.bubbleBot]}>
            <Text style={[styles.bubbleText, m.role === "user" ? styles.bubbleTextUser : null]}>{m.text}</Text>
          </View>
        ))}
        {loading ? <ActivityIndicator color="#0EA5E9" style={{ marginTop: 8 }} /> : null}
      </ScrollView>

      <View style={styles.inputRow}>
        <TextInput
          style={styles.input}
          value={input}
          onChangeText={setInput}
          placeholder={t("help_placeholder")}
          placeholderTextColor="#9CA3AF"
          multiline
          maxLength={500}
          onSubmitEditing={send}
        />
        <Pressable style={styles.sendBtn} onPress={send}>
          <Text style={styles.sendText}>{t("help_send")}</Text>
        </Pressable>
      </View>

      <View style={styles.quickRow}>
        {[
          { key: "help_chip_passcode", query: "Forgot passcode" },
          { key: "help_chip_habit", query: "Add a habit" },
          { key: "help_chip_block", query: "Block apps" },
          { key: "help_chip_community", query: "Community" },
        ].map((q) => (
          <Pressable key={q.key} style={styles.quickChip} onPress={() => void sendText(q.query)}>
            <Text style={styles.quickChipText}>{t(q.key)}</Text>
          </Pressable>
        ))}
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1, backgroundColor: "#fff" },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: Platform.OS === "ios" ? 8 : 0,
    marginBottom: 8,
  },
  title: { fontSize: 24, fontWeight: "800", color: "#111" },
  back: { color: "#0EA5E9", fontWeight: "700", fontSize: 16 },
  subtitle: { fontSize: 13, color: "#6B7280", lineHeight: 19, marginBottom: 12 },
  chatScroll: { flex: 1, maxHeight: 420 },
  chatContent: { paddingBottom: 12 },
  bubble: {
    alignSelf: "flex-start",
    maxWidth: "92%",
    padding: 12,
    borderRadius: 14,
    marginBottom: 8,
    backgroundColor: "#F0F9FF",
    borderWidth: 1,
    borderColor: "#BAE6FD",
  },
  bubbleUser: { alignSelf: "flex-end", backgroundColor: "#0EA5E9", borderColor: "#0EA5E9" },
  bubbleBot: {},
  bubbleText: { fontSize: 14, color: "#0F172A", lineHeight: 20 },
  bubbleTextUser: { color: "#fff" },
  inputRow: { flexDirection: "row", gap: 8, marginTop: 8 },
  input: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    maxHeight: 100,
    backgroundColor: "#FAFAFA",
  },
  sendBtn: {
    backgroundColor: "#0EA5E9",
    paddingHorizontal: 16,
    justifyContent: "center",
    borderRadius: 12,
  },
  sendText: { color: "#fff", fontWeight: "800" },
  quickRow: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 10, marginBottom: 8 },
  quickChip: {
    backgroundColor: "#F1F5F9",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  quickChipText: { fontSize: 12, color: "#334155", fontWeight: "600" },
});
